import { ref } from 'vue'
import { apiClient } from '@shared/utils/apiClient.js'

// Singleton: fetched once, shared across all views
const businessName = ref('')
let businessNameFetched = false

/**
 * Billing composable
 * Provides methods for billing-specific API calls (products, invoices, customers)
 * Mirrors the Flutter BillingService
 */
export function useBilling() {
  // ==================== PRODUCTS ====================

  /**
   * List all products
   */
  const listProducts = async (params = {}) => {
    const query = new URLSearchParams()
    if (params.limit) query.append('limit', params.limit)
    if (params.offset >= 0) query.append('offset', params.offset)
    if (params.search) query.append('search', params.search)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)
    if (params.category_id) query.append('category_id', params.category_id)
    const qs = query.toString()
    return await apiClient.get(`/v1/products${qs ? '?' + qs : ''}`)
  }

  /**
   * Get product by ID
   */
  const getProduct = async (id) => {
    return await apiClient.get(`/v1/products/${id}`)
  }

  /**
   * Create new product
   */
  const createProduct = async (productData) => {
    return await apiClient.post('/v1/products', productData)
  }

  /**
   * Update product
   */
  const updateProduct = async (id, productData) => {
    return await apiClient.put(`/v1/products/${id}`, productData)
  }

  /**
   * Delete product
   */
  const deleteProduct = async (id) => {
    return await apiClient.delete(`/v1/products/${id}`)
  }

  /**
   * Get product by barcode
   */
  const getProductByBarcode = async (barcode) => {
    return await apiClient.get(`/v1/products/barcode/${barcode}`)
  }

  /**
   * List products where stock is at or below minimum threshold
   */
  const listLowStockProducts = async () => {
    return await apiClient.get('/v1/products/low-stock')
  }

  const searchHSN = async (query, limit = 20, businessType = '') => {
    const params = new URLSearchParams()
    if (query) params.append('q', query)
    params.append('limit', limit)
    if (businessType) params.append('business_type', businessType)
    return await apiClient.get(`/v1/hsn/search?${params.toString()}`)
  }

  const getHSNByCode = async (code) => {
    return await apiClient.get(`/v1/hsn/${code}`)
  }

  // ==================== INVOICES ====================

  /**
   * List all invoices
   */
  const listInvoices = async (params = {}) => {
    const query = new URLSearchParams()
    if (params.limit) query.append('limit', params.limit)
    if (params.offset >= 0) query.append('offset', params.offset)
    if (params.search) query.append('search', params.search)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)
    if (params.sale_type) query.append('sale_type', params.sale_type)
    const qs = query.toString()
    return await apiClient.get(`/v1/invoices${qs ? '?' + qs : ''}`)
  }

  /**
   * Get invoice by ID
   */
  const getInvoice = async (id) => {
    return await apiClient.get(`/v1/invoices/${id}`)
  }

  /**
   * Create new invoice
   */
  const createInvoice = async (invoiceData) => {
    return await apiClient.post('/v1/invoices', invoiceData)
  }

  /**
   * Peek the next auto-generated invoice number for a given date.
   * Returns { invoice_number: "INV/YY-YY/NNNN" }. The actual number at
   * creation time may differ by 1 if a concurrent request lands first.
   */
  const peekNextInvoiceNumber = async (date) => {
    const qs = date ? `?date=${encodeURIComponent(date)}` : ''
    return await apiClient.get(`/v1/invoices/next-number${qs}`)
  }

  /**
   * Update invoice
   */
  const updateInvoice = async (id, invoiceData) => {
    return await apiClient.put(`/v1/invoices/${id}`, invoiceData)
  }

  /**
   * Delete invoice
   */
  const deleteInvoice = async (id) => {
    return await apiClient.delete(`/v1/invoices/${id}`)
  }

  /**
   * Finalize invoice (triggers stock decrease, auto-records payment for cash sales)
   */
  const finalizeInvoice = async (id, data = {}) => {
    return await apiClient.post(`/v1/invoices/${id}/finalize`, data)
  }

  /**
   * Void an invoice (reverses stock + loyalty, marks as voided)
   */
  const voidInvoice = async (id, data = {}) => {
    return await apiClient.post(`/v1/invoices/${id}/void`, data)
  }

  /**
   * Get edit history for an invoice
   */
  const getInvoiceEditLogs = async (id) => {
    return await apiClient.get(`/v1/invoices/${id}/edit-logs`)
  }

  /**
   * Get invoice print data
   */
  const getInvoicePrint = async (id) => {
    return await apiClient.get(`/v1/invoices/${id}/print`)
  }

  // ==================== CUSTOMERS ====================

  /**
   * List customers with optional search and pagination
   * @param {Object} options - Query options
   * @param {string} options.search - Search by name or phone
   * @param {number} options.limit - Max results to return
   */
  const listCustomers = async (params = {}) => {
    const query = new URLSearchParams()
    if (params.search) query.append('search', params.search)
    if (params.limit) query.append('limit', params.limit)
    if (params.offset >= 0) query.append('offset', params.offset)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)
    const qs = query.toString()
    return await apiClient.get(`/v1/customers${qs ? '?' + qs : ''}`)
  }

  /**
   * Get customer by ID
   */
  const getCustomer = async (id) => {
    return await apiClient.get(`/v1/customers/${id}`)
  }

  /**
   * Create new customer
   */
  const createCustomer = async (customerData) => {
    return await apiClient.post('/v1/customers', customerData)
  }

  /**
   * Update customer
   */
  const updateCustomer = async (id, customerData) => {
    return await apiClient.put(`/v1/customers/${id}`, customerData)
  }

  /**
   * Delete customer
   */
  const deleteCustomer = async (id) => {
    return await apiClient.delete(`/v1/customers/${id}`)
  }

  /**
   * Get customer ledger (khata) entries for a date range
   */
  const getCustomerLedger = async (customerId, fromDate, toDate) => {
    return await apiClient.get(`/v1/customers/${customerId}/ledger?from_date=${fromDate}&to_date=${toDate}`)
  }

  /**
   * Get vendor ledger entries for a date range
   */
  const getVendorLedger = async (vendorId, fromDate, toDate) => {
    return await apiClient.get(`/v1/billing/vendors/${vendorId}/ledger?from_date=${fromDate}&to_date=${toDate}`)
  }

  // ==================== PAYMENTS ====================

  /**
   * Record a payment against an invoice
   */
  const createPayment = async (paymentData) => {
    return await apiClient.post('/v1/payments', paymentData)
  }

  /**
   * List all payments
   */
  const listPayments = async (params = {}) => {
    const query = new URLSearchParams()
    if (params.limit) query.append('limit', params.limit)
    if (params.offset >= 0) query.append('offset', params.offset)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)
    const qs = query.toString()
    return await apiClient.get(`/v1/payments${qs ? '?' + qs : ''}`)
  }

  /**
   * List payments for a specific invoice
   */
  const listInvoicePayments = async (invoiceId) => {
    return await apiClient.get(`/v1/invoices/${invoiceId}/payments`)
  }

  /**
   * Delete a payment
   */
  const deletePayment = async (id) => {
    return await apiClient.delete(`/v1/payments/${id}`)
  }

  // ==================== BUSINESS ====================

  /**
   * Get business settings
   */
  const getBusiness = async () => {
    return await apiClient.get('/v1/business')
  }

  /**
   * Fetch business name once and cache in singleton ref
   */
  const fetchBusinessName = async () => {
    if (businessNameFetched) return
    try {
      const res = await apiClient.get('/v1/business')
      businessName.value = res?.name || ''
      businessNameFetched = true
    } catch {
      // Silently fail — topbar falls back to "Billing"
    }
  }

  /**
   * Update business settings
   */
  const updateBusiness = async (businessData) => {
    return await apiClient.put('/v1/business', businessData)
  }

  // ==================== CATEGORIES ====================

  /**
   * List all categories
   */
  const listCategories = async (params = {}) => {
    const query = new URLSearchParams()
    if (params.limit) query.set('limit', params.limit)
    if (params.offset >= 0) query.set('offset', params.offset)
    if (params.search) query.set('search', params.search)
    const qs = query.toString()
    return await apiClient.get(`/v1/categories${qs ? `?${qs}` : ''}`)
  }

  /**
   * Get category by ID
   */
  const getCategory = async (id) => {
    return await apiClient.get(`/v1/categories/${id}`)
  }

  /**
   * Get category detail with summary stats (product count, stock, value)
   */
  const getCategoryDetail = async (id) => {
    return await apiClient.get(`/v1/categories/${id}/detail`)
  }

  /**
   * Create new category
   */
  const createCategory = async (categoryData) => {
    return await apiClient.post('/v1/categories', categoryData)
  }

  /**
   * Update category
   */
  const updateCategory = async (id, categoryData) => {
    return await apiClient.put(`/v1/categories/${id}`, categoryData)
  }

  /**
   * Delete category
   */
  const deleteCategory = async (id) => {
    return await apiClient.delete(`/v1/categories/${id}`)
  }

  // ==================== LOYALTY ====================

  /**
   * Get loyalty program settings
   */
  const getLoyaltySettings = async () => {
    return await apiClient.get('/v1/loyalty/settings')
  }

  /**
   * Update loyalty program settings
   */
  const updateLoyaltySettings = async (settingsData) => {
    return await apiClient.put('/v1/loyalty/settings', settingsData)
  }

  /**
   * Get customer loyalty balance and info
   */
  const getCustomerLoyalty = async (customerId) => {
    return await apiClient.get(`/v1/customers/${customerId}/loyalty`)
  }

  /**
   * Get customer loyalty transaction history
   */
  const getCustomerLoyaltyTransactions = async (customerId) => {
    return await apiClient.get(`/v1/customers/${customerId}/loyalty/transactions`)
  }

  /**
   * Adjust customer loyalty points (manual adjustment)
   */
  const adjustCustomerPoints = async (adjustmentData) => {
    return await apiClient.post('/v1/loyalty/adjust', adjustmentData)
  }

  // ==================== VENDORS ====================

  const listVendors = async (params = {}) => {
    const query = new URLSearchParams()
    if (params.limit) query.append('limit', params.limit)
    if (params.offset >= 0) query.append('offset', params.offset)
    if (params.search) query.append('search', params.search)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)
    const qs = query.toString()
    return await apiClient.get(`/v1/billing/vendors${qs ? '?' + qs : ''}`)
  }

  const getVendor = async (id) => {
    return await apiClient.get(`/v1/billing/vendors/${id}`)
  }

  const createVendor = async (vendorData) => {
    return await apiClient.post('/v1/billing/vendors', vendorData)
  }

  const updateVendor = async (id, vendorData) => {
    return await apiClient.put(`/v1/billing/vendors/${id}`, vendorData)
  }

  const deleteVendor = async (id) => {
    return await apiClient.delete(`/v1/billing/vendors/${id}`)
  }

  // ==================== PURCHASE BILLS ====================

  const listPurchaseBills = async (params = {}) => {
    const query = new URLSearchParams()
    if (params.limit) query.append('limit', params.limit)
    if (params.offset >= 0) query.append('offset', params.offset)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)
    if (params.search) query.set('search', params.search)
    const qs = query.toString()
    return await apiClient.get(`/v1/billing/purchase-bills${qs ? '?' + qs : ''}`)
  }

  const getPurchaseBill = async (id) => {
    return await apiClient.get(`/v1/billing/purchase-bills/${id}`)
  }

  const createPurchaseBill = async (billData) => {
    return await apiClient.post('/v1/billing/purchase-bills', billData)
  }

  const updatePurchaseBill = async (id, billData) => {
    return await apiClient.put(`/v1/billing/purchase-bills/${id}`, billData)
  }

  const finalizePurchaseBill = async (id) => {
    return await apiClient.post(`/v1/billing/purchase-bills/${id}/finalize`)
  }

  const cancelPurchaseBill = async (id) => {
    return await apiClient.post(`/v1/billing/purchase-bills/${id}/cancel`)
  }

  // ==================== PURCHASE PAYMENTS ====================

  const createPurchasePayment = async (paymentData) => {
    return await apiClient.post('/v1/billing/purchase-payments', paymentData)
  }

  const listPurchasePayments = async (params = {}) => {
    const query = new URLSearchParams()
    if (params.limit) query.append('limit', params.limit)
    if (params.offset >= 0) query.append('offset', params.offset)
    const qs = query.toString()
    return await apiClient.get(`/v1/billing/purchase-payments${qs ? '?' + qs : ''}`)
  }

  const listPurchaseBillPayments = async (billId) => {
    return await apiClient.get(`/v1/billing/purchase-bills/${billId}/payments`)
  }

  const deletePurchasePayment = async (id) => {
    return await apiClient.delete(`/v1/billing/purchase-payments/${id}`)
  }

  // ==================== DEBIT NOTES ====================

  const createDebitNote = async (noteData) => {
    return await apiClient.post('/v1/billing/debit-notes', noteData)
  }

  const listDebitNotes = async (params = {}) => {
    const query = new URLSearchParams()
    if (params.limit) query.append('limit', params.limit)
    if (params.offset >= 0) query.append('offset', params.offset)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)
    if (params.search) query.set('search', params.search)
    const qs = query.toString()
    return await apiClient.get(`/v1/billing/debit-notes${qs ? '?' + qs : ''}`)
  }

  const getDebitNote = async (id) => {
    return await apiClient.get(`/v1/billing/debit-notes/${id}`)
  }

  const finalizeDebitNote = async (id) => {
    return await apiClient.post(`/v1/billing/debit-notes/${id}/finalize`)
  }

  // ==================== CREDIT NOTES ====================

  const createCreditNote = async (noteData) => {
    return await apiClient.post('/v1/billing/credit-notes', noteData)
  }

  const listCreditNotes = async (params = {}) => {
    const query = new URLSearchParams()
    if (params.limit) query.append('limit', params.limit)
    if (params.offset >= 0) query.append('offset', params.offset)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)
    if (params.search) query.set('search', params.search)
    const qs = query.toString()
    return await apiClient.get(`/v1/billing/credit-notes${qs ? '?' + qs : ''}`)
  }

  const getCreditNote = async (id) => {
    return await apiClient.get(`/v1/billing/credit-notes/${id}`)
  }

  const finalizeCreditNote = async (id) => {
    return await apiClient.post(`/v1/billing/credit-notes/${id}/finalize`)
  }

  // ==================== QUOTATIONS ====================

  const listQuotations = async (params = {}) => {
    const query = new URLSearchParams()
    if (params.limit) query.append('limit', params.limit)
    if (params.offset >= 0) query.append('offset', params.offset)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.sort_order) query.append('sort_order', params.sort_order)
    if (params.search) query.set('search', params.search)
    if (params.status) query.set('status', params.status)
    const qs = query.toString()
    return await apiClient.get(`/v1/billing/quotations${qs ? '?' + qs : ''}`)
  }

  const getQuotation = async (id) => {
    return await apiClient.get(`/v1/billing/quotations/${id}`)
  }

  const createQuotation = async (data) => {
    return await apiClient.post('/v1/billing/quotations', data)
  }

  const updateQuotation = async (id, data) => {
    return await apiClient.put(`/v1/billing/quotations/${id}`, data)
  }

  const deleteQuotation = async (id) => {
    return await apiClient.delete(`/v1/billing/quotations/${id}`)
  }

  const sendQuotation = async (id) => {
    return await apiClient.post(`/v1/billing/quotations/${id}/send`)
  }

  const acceptQuotation = async (id) => {
    return await apiClient.post(`/v1/billing/quotations/${id}/accept`)
  }

  const rejectQuotation = async (id) => {
    return await apiClient.post(`/v1/billing/quotations/${id}/reject`)
  }

  const convertQuotation = async (id) => {
    return await apiClient.post(`/v1/billing/quotations/${id}/convert`)
  }

  // ==================== BANK ACCOUNTS ====================

  const listBankAccounts = async () => {
    return await apiClient.get('/v1/billing/bank-accounts')
  }

  const getBankAccount = async (id) => {
    return await apiClient.get(`/v1/billing/bank-accounts/${id}`)
  }

  const createBankAccount = async (data) => {
    return await apiClient.post('/v1/billing/bank-accounts', data)
  }

  const updateBankAccount = async (id, data) => {
    return await apiClient.put(`/v1/billing/bank-accounts/${id}`, data)
  }

  const deleteBankAccount = async (id) => {
    return await apiClient.delete(`/v1/billing/bank-accounts/${id}`)
  }

  const setDefaultBankAccount = async (id) => {
    return await apiClient.post(`/v1/billing/bank-accounts/${id}/default`)
  }

  // ==================== PURCHASE REPORTS ====================

  const getPurchaseSummary = async (fromDate, toDate) => {
    return await apiClient.get(`/v1/billing/reports/purchase-summary?from_date=${fromDate}&to_date=${toDate}`)
  }

  const getVendorPurchases = async (fromDate, toDate, limit = 20) => {
    return await apiClient.get(`/v1/billing/reports/vendor-purchases?from_date=${fromDate}&to_date=${toDate}&limit=${limit}`)
  }

  const getProductPurchases = async (fromDate, toDate, limit = 20) => {
    return await apiClient.get(`/v1/billing/reports/product-purchases?from_date=${fromDate}&to_date=${toDate}&limit=${limit}`)
  }

  const getDailyPurchases = async (fromDate, toDate) => {
    return await apiClient.get(`/v1/billing/reports/daily-purchases?from_date=${fromDate}&to_date=${toDate}`)
  }

  const getPayablesReport = async () => {
    return await apiClient.get('/v1/billing/reports/payables')
  }

  // ==================== SALES REPORTS ====================

  const getSalesSummary = async (fromDate, toDate) => {
    return await apiClient.get(`/v1/billing/reports/sales-summary?from_date=${fromDate}&to_date=${toDate}`)
  }

  const getProductSales = async (fromDate, toDate, limit = 20) => {
    return await apiClient.get(`/v1/billing/reports/product-sales?from_date=${fromDate}&to_date=${toDate}&limit=${limit}`)
  }

  const getCustomerSales = async (fromDate, toDate, limit = 20) => {
    return await apiClient.get(`/v1/billing/reports/customer-sales?from_date=${fromDate}&to_date=${toDate}&limit=${limit}`)
  }

  const getDailySales = async (fromDate, toDate) => {
    return await apiClient.get(`/v1/billing/reports/daily-sales?from_date=${fromDate}&to_date=${toDate}`)
  }

  const getOutstandingReport = async () => {
    return await apiClient.get('/v1/billing/reports/outstanding')
  }

  // ==================== GST REPORTS ====================

  const getGSTSummary = async (fromDate, toDate) => {
    return await apiClient.get(`/v1/billing/reports/gst-summary?from_date=${fromDate}&to_date=${toDate}`)
  }

  const getHSNSummary = async (fromDate, toDate) => {
    return await apiClient.get(`/v1/billing/reports/hsn-summary?from_date=${fromDate}&to_date=${toDate}`)
  }

  const getGSTFiling = async (year, month) => {
    return await apiClient.get(`/v1/billing/reports/gst-filing?year=${year}&month=${month}`)
  }

  // ==================== BILLING SETTINGS ====================

  /**
   * Get billing-specific settings (unit codes, default unit, etc.)
   */
  const getBillingSettings = async () => {
    return await apiClient.get('/v1/billing/settings')
  }

  /**
   * Update billing-specific settings
   */
  const updateBillingSettings = async (data) => {
    return await apiClient.put('/v1/billing/settings', data)
  }

  // ==================== SIGNATURE ====================

  async function uploadSignature(file) {
    const formData = new FormData()
    formData.append('signature', file)
    const response = await apiClient.post('/v1/billing/signature', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data?.data || response.data
  }

  async function deleteSignature() {
    const response = await apiClient.delete('/v1/billing/signature')
    return response.data?.data || response.data
  }

  async function getSignaturePreview() {
    const response = await apiClient.get('/v1/billing/signature/preview')
    return response.data?.data || response.data
  }

  // ==================== DASHBOARD ====================

  /**
   * Get dashboard statistics with month-over-month comparison
   */
  const getDashboard = async () => {
    return await apiClient.get('/v1/billing/dashboard')
  }

  return {
    // Products
    listProducts,
    getProduct,
    createProduct,
    updateProduct,
    deleteProduct,
    getProductByBarcode,
    listLowStockProducts,
    searchHSN,
    getHSNByCode,

    // Invoices
    listInvoices,
    getInvoice,
    createInvoice,
    peekNextInvoiceNumber,
    updateInvoice,
    deleteInvoice,
    finalizeInvoice,
    voidInvoice,
    getInvoiceEditLogs,
    getInvoicePrint,

    // Customers
    listCustomers,
    getCustomer,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    getCustomerLedger,
    getVendorLedger,

    // Payments
    createPayment,
    listPayments,
    listInvoicePayments,
    deletePayment,

    // Business
    businessName,
    fetchBusinessName,
    getBusiness,
    updateBusiness,

    // Categories
    listCategories,
    getCategory,
    getCategoryDetail,
    createCategory,
    updateCategory,
    deleteCategory,

    // Loyalty
    getLoyaltySettings,
    updateLoyaltySettings,
    getCustomerLoyalty,
    getCustomerLoyaltyTransactions,
    adjustCustomerPoints,

    // Vendors
    listVendors,
    getVendor,
    createVendor,
    updateVendor,
    deleteVendor,

    // Purchase Bills
    listPurchaseBills,
    getPurchaseBill,
    createPurchaseBill,
    updatePurchaseBill,
    finalizePurchaseBill,
    cancelPurchaseBill,

    // Purchase Payments
    createPurchasePayment,
    listPurchasePayments,
    listPurchaseBillPayments,
    deletePurchasePayment,

    // Debit Notes
    createDebitNote,
    listDebitNotes,
    getDebitNote,
    finalizeDebitNote,

    // Credit Notes
    createCreditNote,
    listCreditNotes,
    getCreditNote,
    finalizeCreditNote,

    // Quotations
    listQuotations,
    getQuotation,
    createQuotation,
    updateQuotation,
    deleteQuotation,
    sendQuotation,
    acceptQuotation,
    rejectQuotation,
    convertQuotation,

    // Bank Accounts
    listBankAccounts,
    getBankAccount,
    createBankAccount,
    updateBankAccount,
    deleteBankAccount,
    setDefaultBankAccount,

    // Purchase Reports
    getPurchaseSummary,
    getVendorPurchases,
    getProductPurchases,
    getDailyPurchases,
    getPayablesReport,

    // Sales Reports
    getSalesSummary,
    getProductSales,
    getCustomerSales,
    getDailySales,
    getOutstandingReport,

    // GST Reports
    getGSTSummary,
    getHSNSummary,
    getGSTFiling,

    // Billing Settings
    getBillingSettings,
    updateBillingSettings,

    // Signature
    uploadSignature,
    deleteSignature,
    getSignaturePreview,

    // Dashboard
    getDashboard,
  }
}
